import React, { useEffect, useMemo, useRef, useState } from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { Flex } from 'src/components/flex';
import Dialog from 'src/components/dialog/Dialog';
import Chip from 'src/components/chip';

export type EditableLabel = {
    id: number;
    name: string;
    color: string;
};

export type EditableItem = {
    id: string | number;
    name: string;
    labels: EditableLabel[];
};

type Props = {
    open: boolean;
    onClose: () => void;
    title: string;
    items: EditableItem[];
    allLabels: EditableLabel[];
    warningText: string;
    isSaving: boolean;
    onSave: (updatedItems: EditableItem[]) => Promise<void>;
};

export default function BulkEditLabelsModal({
    open,
    onClose,
    title,
    items,
    allLabels,
    warningText,
    isSaving,
    onSave,
}: Props) {
    const [itemStates, setItemStates] = useState<EditableItem[]>(items);
    const [hasTouched, setHasTouched] = useState(false);
    const itemsRef = useRef(items);
    itemsRef.current = items;

    useEffect(() => {
        if (!open) {
            return;
        }

        setItemStates(itemsRef.current);
        setHasTouched(false);
    }, [open]);

    const value = useMemo(() => {
        return allLabels.filter((label) =>
            itemStates.some((item) => item.labels.some((l) => l.id === label.id))
        );
    }, [allLabels, itemStates]);

    const handleLabelToggle = (label: EditableLabel) => {
        const isAdding = !value.some((v) => v.id === label.id);
        setHasTouched(true);

        if (isAdding) {
            setItemStates((prev) => prev.map((item) => {
                if (!item.labels.some((l) => l.id === label.id)) {
                    return { ...item, labels: [...item.labels, label] };
                }
                return item;
            }));
        } else {
            setItemStates((prev) => prev.map((item) => ({
                ...item,
                labels: item.labels.filter((l) => l.id !== label.id),
            })));
        }
    };

    const handleRemoveTag = (label: EditableLabel) => {
        setHasTouched(true);
        setItemStates((prev) => prev.map((item) => ({
            ...item,
            labels: item.labels.filter((l) => l.id !== label.id),
        })));
    };

    const save = async () => {
        await onSave(itemStates);
    };

    const oneItemSelected = items.length === 1;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{ sx: { width: '100%' } }}
        >
            <DialogTitle>{title}</DialogTitle>

            <DialogContent>
                {items.length > 1 && (
                    <Accordion elevation={0} defaultExpanded sx={{ mb: 4 }}>
                        <AccordionSummary expandIcon={<KeyboardArrowDownIcon />} sx={{ px: 0 }}>
                            Current Labels
                        </AccordionSummary>

                        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: 0 }}>
                            {itemStates.map((item) => (
                                <Flex key={item.id} alignItems="flex-start" columnGap={1} rowGap={0}>
                                    <Typography
                                        variant="body2"
                                        sx={{ minWidth: 140, maxWidth: 140, fontWeight: 500, pt: 0.25, wordBreak: 'break-word' }}
                                    >
                                        {item.name}
                                    </Typography>
                                    <Typography variant="body2" sx={{ pt: 0.25, color: 'text.secondary' }}>—</Typography>
                                    <Flex sx={{ flexWrap: 'wrap', gap: 0.5, flex: 1 }}>
                                        {item.labels.length ? item.labels.map((label) => (
                                            <Chip
                                                key={label.id}
                                                label={label.name}
                                                color={label.color}
                                                size="small"
                                            />
                                        )) : (
                                            <Typography variant="body2" sx={{ color: 'text.disabled' }}>No labels</Typography>
                                        )}
                                    </Flex>
                                </Flex>
                            ))}
                        </AccordionDetails>
                    </Accordion>
                )}

                <Autocomplete
                    multiple
                    options={allLabels}
                    value={value}
                    onChange={(_, __, reason) => {
                        if (reason === 'clear') {
                            setHasTouched(true);
                            setItemStates((prev) => prev.map((item) => ({ ...item, labels: [] })));
                        }
                    }}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, val) => option.id === val.id}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Search labels"
                            variant="standard"
                            autoFocus
                        />
                    )}
                    renderOption={(_props, option) => {
                        const someItemsHaveLabel = itemStates.some((item) =>
                            item.labels.some((l) => l.id === option.id)
                        );
                        const someItemsDoNotHaveLabel = itemStates.some((item) =>
                            !item.labels.some((l) => l.id === option.id)
                        );
                        const selected = value.some((v) => v.id === option.id);

                        return (
                            <MenuItem
                                key={option.id}
                                onClick={() => handleLabelToggle(option)}
                                sx={{ bgcolor: selected ? 'grey.200' : 'transparent' }}
                            >
                                {!oneItemSelected && (
                                    <Checkbox
                                        checked={someItemsHaveLabel}
                                        indeterminate={someItemsHaveLabel && someItemsDoNotHaveLabel}
                                        disabled
                                        sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
                                    />
                                )}
                                <Chip label={option.name} color={option.color} size="small" />
                            </MenuItem>
                        );
                    }}
                    renderTags={(tags, getTagProps) => tags.map((option, index) => {
                        const someItemsHaveLabel = itemStates.some((item) =>
                            item.labels.some((l) => l.id === option.id)
                        );
                        const someItemsDoNotHaveLabel = itemStates.some((item) =>
                            !item.labels.some((l) => l.id === option.id)
                        );

                        return (
                            <Chip
                                {...getTagProps({ index })}
                                key={option.id}
                                label={(
                                    <Flex alignItems="center" columnGap={0.5}>
                                        {!oneItemSelected && (
                                            <Checkbox
                                                checked={someItemsHaveLabel}
                                                indeterminate={someItemsHaveLabel && someItemsDoNotHaveLabel}
                                                disabled
                                                sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
                                            />
                                        )}
                                        <Typography variant="body2" sx={{ color: option.color }}>
                                            {option.name}
                                        </Typography>
                                    </Flex>
                                )}
                                onDelete={() => handleRemoveTag(option)}
                                color={option.color}
                                size="small"
                            />
                        );
                    })}
                    sx={{
                        '& .MuiChip-root': { height: '24px' },
                        '& .MuiSvgIcon-root.MuiChip-deleteIcon': { mx: 0.5 },
                    }}
                />

                <DialogContentText sx={{ color: 'red', fontSize: 'small', mt: 2 }}>
                    {warningText}
                </DialogContentText>
            </DialogContent>

            <DialogActions>
                <Button
                    variant="outlined"
                    onClick={onClose}
                    disabled={isSaving}
                >
                    Cancel
                </Button>

                <LoadingButton
                    variant="contained"
                    onClick={save}
                    disabled={!hasTouched}
                    loading={isSaving}
                >
                    Save
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}
